<template>
  <div class="entity-card smooth-shadow">
    <div class="entity-selection-contents">
      <div class="header">
        <!-- <i class="material-icons entity-icon md-18">device_hub</i> -->
        <div class="flex-col">
          <input
            :class="[
              'title',
              'eliipsis',
              { 'disable-clicks': !data.isEditable },
            ]"
            :ref="data.id"
            v-model="modelName"
            :placeholder="data.name"
            @blur="updateLayerName"
          />
          <!-- <p class="caption">{{ `${data.entity_name} Entity` }}</p> -->
        </div>

        <expand />
        <!-- <icon
          v-if="data.isEditable"
          size="18"
          type="button"
          class="header-icon delete-icon"
          @click="deleteLayer(data)"
          >delete</icon
        > -->
        <div class="more-options">
          <icon
            v-if="data.isEditable"
            size="18"
            type="button"
            class="header-icon"
            @click="toggleOptions"
            >more_vert</icon
          >
          <SlideYUpTransition>
            <ul
              v-if="showOptions"
              class="options popup-shadow"
              v-click-outside="toggleOptions"
            >
              <li>
                <icon size="18" @click="performAction('edit')"
                  >filter_list</icon
                >
                Default Filters
              </li>
              <li @click="performAction('rename')">
                <icon size="18">edit</icon>Rename
              </li>
              <!-- <li><icon size="18">file_copy</icon>Duplicate</li> -->
              <li @click="performAction('delete')">
                <icon size="18">delete</icon>Delete
              </li>
            </ul>
          </SlideYUpTransition>
        </div>
      </div>
      <div class="filters" v-if="getFilterChits && getFilterChits.length > 0">
        <span
          v-for="(chit, index) in getFilterChits"
          :key="index"
          class="chit"
          >{{ chit }}</span
        >
      </div>
      <div class="contents">
        <div
          v-if="!data.layers || data.layers.length === 0"
          class="no-content-image"
        >
          <!-- <img src="@/assets/dashboard_created.svg" class="image" /> -->
          <p>
            No properties added. Click below to start visualizing your entity
            properties.
          </p>
        </div>

        <!-- Entity layers -->
        <div v-for="item in data.layers" :key="item.id">
          <EntityPropLayer
            :data="item"
            :properties="getColumns"
            :aggregations="getAggregations"
            :isActive="primaryActiveLayers.includes(item.id)"
            @setActive="setActiveLayer"
          />
        </div>
      </div>
      <div v-if="data.isEditable" class="footer">
        <button class="footer-add-button" @click="createEntityProperty()">
          <icon>add</icon>Add {{ data.entity_name }} property
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import EntityPropLayer from "@/components/metrics/views/EntityPropLayer";
import { EntityPropertyLayer } from "@/models/Layers.model";
import { startTour } from "@/components/metrics/tours/EntityModelCard.tour";
import { SlideYUpTransition } from "vue2-transitions";

export default {
  props: {
    data: Object,
  },
  components: {
    EntityPropLayer,
    SlideYUpTransition,
  },
  data() {
    return {
      modelName: this.data.name,
      showOptions: false,
    };
  },
  computed: {
    ...mapGetters("Entities", ["getEntities"]),
    ...mapGetters("Metrics", ["getAggregations"]),
    ...mapGetters("Datasources", ["getDataSources", "getDataSourceDetails"]),
    ...mapGetters("LayerManager", ["getActiveLayers"]),
    ...mapGetters("FilterManager", ["getAppliedFilters"]),
    primaryActiveLayers() {
      return this.getActiveLayers
        .filter((item) => item.type === "primary")
        .map((item) => item.id);
    },
    getFilterChits() {
      return this.getAppliedFilters
        .filter((item) => item.entity_model_id === this.data.id)
        .map((item) => {
          return `${item.category_id.split("__")[1]} ${item.comparison} ${
            item.item_id
          }`;
        });
    },
    getColumns() {
      // check if entity id and all the other data required to fetch the columns are present
      if (
        !(
          this.getEntities &&
          this.getEntities.length > 0 &&
          this.getDataSources &&
          this.data.entity_id
        )
      ) {
        return;
      }

      const columns = [];
      // get the details of the selected entity
      const selectedEntity = this.getEntities.find(
        (item) => item.id === this.data.entity_id
      );
      if (selectedEntity) {
        // get all the datasource details of the selected entity
        selectedEntity.datasources.map((datasource) => {
          // get all the selected columns of the entity from each of its datasource
          const dataSourceDetails = this.getDataSourceDetails(
            datasource.datasource_id
          );

          if (dataSourceDetails && dataSourceDetails.columns) {
            dataSourceDetails.columns.forEach((column) => {
              // add the columns only if it's a measure
              if (
                datasource.selected_cols.includes(column.name) &&
                column.is_measure
              ) {
                columns.push(column);
              }
              // check if the column is an entity id col, then push that
              else if (
                datasource.type === "base" &&
                column.name === selectedEntity.entity_id_col
              ) {
                columns.push({
                  ...column,
                  aggs: [
                    {
                      id: "count_unique",
                      name: "count_unique",
                    },
                    {
                      id: "count",
                      name: "count",
                    },
                  ],
                });
              }
            });
          }
        });
      }

      return columns;
    },
  },
  methods: {
    ...mapActions("LayerManager", [
      "addEntityProp",
      "updateLayer",
      "deleteLayer",
      "setActiveLayer",
    ]),
    ...mapActions("Entities", ["fetchEntities"]),
    ...mapActions("Datasources", ["fetchDataSources"]),
    ...mapActions("Metrics", ["fetchExtras"]),
    toggleOptions() {
      this.showOptions = !this.showOptions;
    },
    performAction(action) {
      switch (action) {
        case "rename":
          this.$refs[this.data.id].focus();
          break;
        case "delete":
          this.deleteLayer(this.data);
          break;
      }
      this.toggleOptions();
    },
    createEntityProperty() {
      this.addEntityProp(
        new EntityPropertyLayer({
          name: "Untitled layer",
          entity_model_id: this.data.id,
          property: null,
          aggregation: null,
          value: 0,
        })
      );
    },
    updateLayerName() {
      this.updateLayer({
        id: this.data.id,
        type: this.data.type,
        name: this.modelName,
      });
    },
  },
  mounted() {
    if (this.data.isEditable) {
      // fetch data for entities
      if (!this.getEntities || this.getEntities.length === 0) {
        this.fetchEntities();
      }

      // fetch all data sources
      if (!this.getDataSources || this.getDataSources.length === 0) {
        this.fetchDataSources();
      }

      // fetch all aggregations for the columns
      if (!this.getAggregations || this.getAggregations.length === 0) {
        this.fetchExtras();
      }
    }

    // if (this.$route.fullPath === "/studio/edit") {
    //   startTour();
    // }
  },
};
</script>

<style lang="scss" scoped>
.entity-card {
  background-color: var(--color-secondary);
  margin: 5px 10px 5px 10px;
  border-radius: 5px;

  .entity-selection-contents {
    display: flex;
    flex-direction: column;

    .header {
      display: flex;
      flex-direction: row;
      align-items: center;
      border-bottom: 1px solid var(--border-color);
      padding: 5px;

      .entity-icon {
        border-radius: 50%;
        margin-left: 10px;
        opacity: 0.5;
      }

      .delete-icon {
        display: none;
      }

      .more-options {
        position: relative;

        .options {
          position: absolute;
          display: flex;
          flex-direction: column;
          list-style-type: none;
          padding: 0;
          margin: 0;
          top: 30px;
          right: 0;
          width: 170px;
          background: var(--color-secondary-light);
          border: 1px solid var(--border-color);
          z-index: 1;
          border-radius: 5px;

          li {
            padding: 15px;
            display: flex;
            align-items: center;

            i {
              margin-right: 10px;
            }

            &:hover {
              cursor: pointer;
              color: var(--color-primary);
              background: var(--border-color);
            }
          }
        }
      }

      &:hover .delete-icon {
        display: flex !important;
      }

      input.title {
        margin: 0;
        font-weight: bold;
        text-transform: capitalize;
        padding: 7px 10px;
        margin: 0 5px;
        min-width: 0;
        width: auto;
        background: transparent;
        border: 1px solid transparent;
        color: var(--font-color);
        border-radius: 3px;
        font-size: 0.8rem;

        &.editable:hover {
          background: var(--color-default-background);
          border: 1px solid var(--color-accent);
        }

        &:focus {
          border: 1px solid var(--color-accent);
        }
      }

      .disable-clicks {
        pointer-events: none;
      }

      .caption {
        margin: 0;
        padding: 0 10px 0 15px;
        opacity: 0.6;
      }

      h4 {
        font-size: 1em;
      }
    }

    .filters {
      padding: 10px 15px;
      border-bottom: 1px solid var(--border-color);
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;

      span.chit {
        font-size: 0.9em;
        background-color: var(--color-primary-light);
        padding: 3px 10px;
        border-radius: 3px;
        color: var(--color-primary);
        margin: 0 5px 5px 0;
      }
    }

    .contents {
      display: flex;
      flex-direction: column;

      .no-content-image {
        display: flex;
        flex-direction: column;
        padding: 15px;
        justify-content: center;
        align-items: center;

        .image {
          height: 200px;
          width: auto;
        }
        p {
          margin: 0;
          opacity: 0.7;
          margin-top: 10px;
          text-align: center;
        }
      }
    }

    .footer {
      display: flex;
      border-radius: 0 0 5px 5px;
      margin: 5px;

      .footer-add-button {
        flex: 1;
        border: 0;
        background: transparent;
        border-radius: 5px;
        padding: 10px 15px;
        color: var(--color-primary);
        transition: 0.3s all ease-in-out;
        outline: none;
        display: flex;
        align-items: center;
        justify-content: center;
        text-transform: capitalize;

        i {
          margin-right: 10px;
          opacity: 0.6;
        }

        &:hover {
          cursor: pointer;
          opacity: 0.7;
        }

        &:active {
          transform: scale(0.97);
        }
      }
    }
  }
}
</style>
